import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import styles from "./css/CityInfoTab.module.css";
import { GraphsCard } from "@/components/cards/GraphsCard";

import { GaugeChart } from "@/components/graphs/GaugeChart";
import { GaugeChartEnergyCreation } from "@/components/graphs/GaugeChartEnergyCreation";
import { CityAttributes, Favorites } from "@/types";
import { SimpleYearComparisonChart } from "@/components/graphs/SimpleYearComparisonGraph";
import { GroupedYearComparisonChart } from "@/components/graphs/GroupedYearComparisonChart";

interface CityInfoTabProps {
  cityAttributes: CityAttributes | null;
  favorites: Favorites;
  setFavorites: (favorites: Favorites) => void;
  colors : {
    usage: {
      [key: string]: string;
    },
    vehicles: {
      [key: string]: string;
    },
    residents: {
      [key: string]: string;
    },
    apartments: {
      [key: string]: string;
    }
  };
}

export const CityInfoTab: React.FC<CityInfoTabProps> = ({ cityAttributes, favorites, setFavorites, colors }) => {
  const { t } = useTranslation('CityInfoTab');

  // Dynamically extract years from data
  const years = cityAttributes?.energy_resilience ? Object.keys(cityAttributes.energy_resilience).sort() : [];
  const futureYear = years[1] || "2040";

  const setFavourite = (id: string, isFavourite: boolean) => {
    setFavorites({ ...favorites, [id]: isFavourite });
  };

  return (
    <div className={styles.container}>
      <GraphsCard 
        title={t('residents')}
        children={<SimpleYearComparisonChart data={cityAttributes?.population_count_dict} />} 
        setFavourite={setFavourite} 
        isFavourite={favorites?.residents ?? false} 
        id="residents"
      />
      <GraphsCard 
        title={t('vehicles')}
        children={<SimpleYearComparisonChart data={cityAttributes?.vehicles_count_dict} />} 
        setFavourite={setFavourite} 
        isFavourite={favorites?.vehicles ?? false} 
        id="vehicles"
      />
      <GraphsCard 
        title={t('residentialUnits')}
        children={<SimpleYearComparisonChart data={cityAttributes?.residential_units_count_dict} />} 
        setFavourite={setFavourite} 
        isFavourite={favorites?.apartments ?? false} 
        id="apartments"
      />
      <GraphsCard 
        title={t('metersByUsage')}
        children={<GroupedYearComparisonChart data={cityAttributes?.areas_dict} />} 
        setFavourite={setFavourite} 
        isFavourite={favorites?.usage ?? false} 
        id="usage"
        disclaimer={{
          text: t('cbsDisclaimer'),
          type: "info"
        }}
      />
      <GraphsCard 
        title={t('energyResilience')}
        children={<GaugeChart
          data={cityAttributes?.energy_resilience?.[futureYear]?.max}
          needle_value={cityAttributes?.energy_resilience?.[years[0] || "2025"]?.needle ?? 0}
          second_needle_value={cityAttributes?.energy_resilience?.[futureYear]?.second_needle ?? 0}
          year="מגה וואט"
        />}
        setFavourite={setFavourite} 
        isFavourite={favorites?.energy ?? false} 
        id="energy"
        disclaimer={{
          text: t('energyIndexDisclaimer'),
          type: "info"
        }}
        subtitle={t('renewableSubtitle')}
      />
      <GraphsCard 
        title={t('renewableEnergyGeneration')} 
        children={<GaugeChartEnergyCreation
          data={cityAttributes?.renewable_energy_generation?.potential_production}
          needle_value={cityAttributes?.renewable_energy_generation?.existing_production ?? 0}
          year="מגה וואט"
        />}
        setFavourite={setFavourite} 
        isFavourite={favorites?.energyCreation ?? false} 
        id="energyCreation"
      />
    </div>  
  );
};
